import React, { useState } from "react";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { AdminPanelLabel } from "./types";
import { LabelOutputType, LabelType } from "../../context/ConfigContext/types";
import LabelValueCard from "./LabelValueCard";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import ClearIcon from "@mui/icons-material/Clear";
import { useAdminPanelContext } from "../../context/AdminPanelContext";
import { confirmAlert } from "react-confirm-alert";

import "./LabelCard.css";
import "react-confirm-alert/src/react-confirm-alert.css";

interface LabelCardProps {
  label: AdminPanelLabel;
}

enum LabelAction {
  SAVE = "Save",
  DELETE = "Delete",
  UPDATE_ACTIVE = "Update active flag on",
}

const LabelCard: React.FC<LabelCardProps> = ({ label }) => {
  const { handleLabels, handleLabelValues, selectedTopic } = useAdminPanelContext();
  const [expanded, setExpanded] = useState(false);
  const [editLabel, setEditLabel] = useState(label.id < 0);
  const [newLabel, setNewLabel] = useState({ ...label });

  const handleAddButtonClick = async () => {
    const lowestId = Math.min(...label.possibleLabelValues.map((plv) => plv.id));
    const newUnsavedId = lowestId < 0 ? lowestId - 1 : -1;
    await handleLabelValues.add(label.id, { id: newUnsavedId, value: "", active: true }, false);
  };

  const undoLabelEdit = () => {
    setEditLabel(!editLabel);
    setNewLabel({ ...label });
  };

  const handleLabelSave = async () => {
    await handleLabels.add({ ...newLabel, topicId: selectedTopic });
    setEditLabel(false);
  };

  const handleLabelDelete = async () => {
    if (label.id > 0) return;
    handleLabels.delete(label.id);
    setEditLabel(false);
  };

  const handleUpdateActive = async () => {
    if (label.id < 0) return;
    handleLabels.update({ ...label, active: !label.active }, true);
  };

  const submit = (action: LabelAction) => {
    const confirmAlertObj = {
      title: `${action} label`,
      message: `Are you sure you want to ${action} label id ${label.id}`,
      buttons: [
        {
          label: "Confirm",
          onClick: () => null,
        },
        {
          label: "Cancel",
        },
      ],
    };
    if (action === LabelAction.SAVE) confirmAlertObj.buttons[0].onClick = handleLabelSave;
    if (action === LabelAction.DELETE) confirmAlertObj.buttons[0].onClick = handleLabelDelete;
    if (action === LabelAction.UPDATE_ACTIVE) confirmAlertObj.buttons[0].onClick = handleUpdateActive;
    confirmAlert(confirmAlertObj);
  };

  return (
    <Card sx={{ display: "flex", marginLeft: "64px", marginRight: "64px" }}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <CardContent sx={{ display: "flex", flexDirection: "row" }}>
          <IconButton disabled={label.id < 0} onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          {!editLabel ? (
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Tooltip
                arrow
                title={
                  <Typography variant="caption">
                    Label:
                    <br />
                    Id: {label.id}
                    <br />
                    Type: {label.type}
                    <br />
                    Output type: {label.outputType}
                    <br />
                    Value: {label.name}
                  </Typography>
                }
              >
                <Typography component="div" variant="h5">
                  {label.name}
                </Typography>
              </Tooltip>
              <FormControlLabel
                value="Active"
                control={<Switch color="success" checked={label.active} />}
                onChange={() => submit(LabelAction.UPDATE_ACTIVE)}
                label={label.active ? "Active" : "Inactive"}
                labelPlacement="end"
                sx={{ ml: 1 }}
              />
              {/* <IconButton disabled size="medium" onClick={() => setEditLabel(!editLabel)}>
                <EditIcon />
              </IconButton> */}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <TextField
                fullWidth
                sx={{ marginRight: 1 }}
                id="label-name"
                label="Label name"
                variant="outlined"
                value={newLabel.name}
                onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })}
              />

              <FormControl fullWidth sx={{ marginRight: 1 }}>
                <InputLabel>Label type</InputLabel>
                <Select
                  label="Label type"
                  autoWidth
                  disabled
                  value={newLabel.type || ""}
                  onChange={(e) => setNewLabel({ ...newLabel, type: e.target.value as LabelType })}
                >
                  {Object.values(LabelType).map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ marginRight: 1, minWidth: 180 }}>
                <InputLabel>Output type</InputLabel>
                <Select
                  label="Output type"
                  autoWidth
                  disabled
                  value={newLabel.outputType || ""}
                  onChange={(e) => setNewLabel({ ...newLabel, outputType: e.target.value as LabelOutputType })}
                >
                  {Object.values(LabelOutputType).map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                value="Active"
                control={<Switch color="success" checked={newLabel.active} />}
                onChange={() => setNewLabel({ ...newLabel, active: !newLabel.active })}
                label={label.active ? "Active" : "Inactive"}
                labelPlacement="end"
                sx={{ ml: 1 }}
              />

              <Tooltip title="Undo edit">
                <Box sx={{ paddingTop: "7px" }}>
                  <IconButton size="medium" onClick={undoLabelEdit} disabled={label.id < 0}>
                    <UndoIcon />
                  </IconButton>
                </Box>
              </Tooltip>

              <Tooltip title="Delete label">
                <Box sx={{ paddingTop: "7px" }}>
                  <IconButton size="medium" color="error" onClick={() => submit(LabelAction.DELETE)}>
                    <ClearIcon />
                  </IconButton>
                </Box>
              </Tooltip>
              <Tooltip title="Save changes">
                <Box sx={{ paddingTop: "7px" }}>
                  <IconButton
                    size="medium"
                    color="success"
                    onClick={() => submit(LabelAction.SAVE)}
                    disabled={!newLabel.name || !newLabel.type || !newLabel.outputType}
                  >
                    <CheckIcon />
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          )}
        </CardContent>

        {expanded && (
          <CardContent>
            <Grid container spacing={2}>
              <Grid item>
                <Card variant="outlined" style={{ height: "66px" }}>
                  <Button
                    onClick={handleAddButtonClick}
                    variant="outlined"
                    fullWidth
                    disabled={label.outputType === LabelOutputType.RELEVANCY}
                    style={{ height: "100%" }}
                  >
                    <AddIcon fontSize="small" />
                  </Button>
                </Card>
              </Grid>
              {label.possibleLabelValues.map((labelValue) => (
                <Grid item key={labelValue.id}>
                  <LabelValueCard label={label} labelValue={labelValue}></LabelValueCard>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        )}
      </Box>
    </Card>
  );
};

export default LabelCard;
